import {
  getFeatureFlags,
  selectIsEmbeddedMessagingJourneysFeatureFlagOn,
  selectIsQuietHoursAbortCampaignFeatureFlagOn,
  selectIsQuietHoursFeatureFlagOn,
  selectNBAJourneysEnabled,
} from 'core/userContext';
import { getIsTemplateValidationEnabled } from 'modules/workflows/ducks';
import { ReactNode, createContext } from 'react';
import { useSelector } from 'react-redux';
import { BaseSendEditorProps } from '../BaseSendEditor.types';
import {
  checkIsSendEditorImprovementsEnabled,
  isStudioTemplateEditingEnabled,
} from '../BaseSendEditor.utils';

export interface FeatureFlagContextState extends Partial<BaseSendEditorProps> {
  campaignRateLimitingIsEnabled: boolean;
  isJourneysCampaignPerformanceAlertsEnabled: boolean;
  isTemplateValidationEnabled: boolean;
  quietHoursIsEnabled: boolean;
  quietHoursAbortCampaignIsEnabled: boolean;
  studioTemplateEditingIsEnabled: boolean;
  isCampaignPerformanceAlertsEnabled: boolean;
  isCampaignAlertOverridesEnabled: boolean;
  isEmbeddedMessagingJourneysEnabled?: boolean;
  isSendEditorImprovementsEnabled?: boolean;
  isNBAJourneysEnabled?: boolean;
  isWhatsAppGAEnabled?: boolean;
}

export const FeatureFlagContext = createContext<FeatureFlagContextState>({
  campaignRateLimitingIsEnabled: false,
  isJourneysCampaignPerformanceAlertsEnabled: false,
  isTemplateValidationEnabled: false,
  quietHoursIsEnabled: false,
  quietHoursAbortCampaignIsEnabled: false,
  studioTemplateEditingIsEnabled: false,
  isCampaignPerformanceAlertsEnabled: false,
  isCampaignAlertOverridesEnabled: false,
  isEmbeddedMessagingJourneysEnabled: false,
  isSendEditorImprovementsEnabled: false,
  isNBAJourneysEnabled: false,
  isWhatsAppGAEnabled: false,
});

export const FeatureFlagContextProvider = ({
  children,
  messageMedium,
}: Partial<BaseSendEditorProps> & { children: ReactNode }) => {
  const featureFlags = useSelector(getFeatureFlags);
  const campaignRateLimitingIsEnabled = !!featureFlags?.CampaignRateLimiting;
  const isCampaignPerformanceAlertsEnabled = !!featureFlags?.CampaignPerformanceAlerts;
  const isJourneysCampaignPerformanceAlertsEnabled =
    !!featureFlags?.JourneysCampaignPerformanceAlerts;
  const isCampaignAlertOverridesEnabled = !!featureFlags?.CampaignAlertOverrides;
  const isWhatsAppGAEnabled = !!featureFlags?.WhatsAppMessageChannelGA;
  const quietHoursIsEnabled = useSelector(selectIsQuietHoursFeatureFlagOn);
  const quietHoursAbortCampaignIsEnabled = useSelector(
    selectIsQuietHoursAbortCampaignFeatureFlagOn,
  );
  const studioTemplateEditingIsEnabled = isStudioTemplateEditingEnabled(
    messageMedium as BaseSendEditorProps['messageMedium'],
    featureFlags,
  );
  const isTemplateValidationEnabled = useSelector(getIsTemplateValidationEnabled);
  const isEmbeddedMessagingJourneysEnabled = useSelector(
    selectIsEmbeddedMessagingJourneysFeatureFlagOn,
  );
  const isSendEditorImprovementsEnabled = checkIsSendEditorImprovementsEnabled(
    messageMedium as BaseSendEditorProps['messageMedium'],
    featureFlags,
  );

  const isNBAJourneysEnabled = useSelector(selectNBAJourneysEnabled);

  return (
    <FeatureFlagContext.Provider
      value={{
        campaignRateLimitingIsEnabled,
        isCampaignPerformanceAlertsEnabled,
        isJourneysCampaignPerformanceAlertsEnabled,
        isCampaignAlertOverridesEnabled,
        isTemplateValidationEnabled,
        quietHoursIsEnabled,
        quietHoursAbortCampaignIsEnabled,
        studioTemplateEditingIsEnabled,
        isEmbeddedMessagingJourneysEnabled,
        isSendEditorImprovementsEnabled,
        isNBAJourneysEnabled,
        isWhatsAppGAEnabled,
      }}
    >
      {children}
    </FeatureFlagContext.Provider>
  );
};
